import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SelectOption } from '@app/app/shared/styleguide/components/selects/sg-select/sg-select.component';
import { TranslocoService } from '@ngneat/transloco';
import {
  AttendanceRequest,
  AttendanceType,
  BooleanFilter,
  BooleanQueryRequest,
  BooleanQueryType,
  NotRequest,
  OrRequest,
} from '../../../../models/admin/boolean-filters.model';

@Component({
  selector: 'ptl-v3-attendance-filter',
  templateUrl: './v3-attendance-filter.component.html',
  styleUrls: ['./v3-attendance-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V3AttendanceFilterComponent implements OnInit {
  @Input() selectedFilters: BooleanFilter[];
  @Input() isPlaylistPageAndTicketExist: boolean;

  @Output() filterDeleted = new EventEmitter<void>();
  @Output() filterSaved = new EventEmitter<BooleanFilter>();

  selectedAttendance: AttendanceType[] = [];
  attendanceIs = true;
  attendances: { type: AttendanceType; value: boolean; label: string }[] = [];
  isOptions: SelectOption<boolean>[] = [];

  constructor(private translocoService: TranslocoService) {}

  ngOnInit() {
    this.getSettingsFromFilters();
    this.initAttendances();
    this.isOptions = [
      { value: true, label: this.translocoService.translate('translations.filter.is') },
      { value: false, label: this.translocoService.translate('translations.filter.isNot') },
    ];
  }

  onAttendanceIsChange(attendanceIs: boolean) {
    this.attendanceIs = attendanceIs;
  }

  changeAttendance(attendanceType: AttendanceType) {
    this.attendances = this.attendances.map((attendance) => {
      if (attendanceType === attendance.type) {
        attendance.value = !attendance.value;
      }
      return attendance;
    });
  }

  deleteFilter() {
    this.filterDeleted.emit();
  }

  applyFilter() {
    const selectedAttendances = this.attendances.filter((attendance) => attendance.value);
    const request = this.getRequest(selectedAttendances.map((attendance) => attendance.type));
    const message = this.getMessage(selectedAttendances.map((status) => status.label));
    this.filterSaved.emit({ request: request, message: message, type: 'ATTENDANCE' });
  }

  private getRequest(attendances: AttendanceType[]): BooleanQueryRequest {
    const attendanceRequest: AttendanceRequest[] = attendances.flatMap((attendance) => {
      const request: AttendanceRequest = { type: BooleanQueryType.ATTENDANCE, attendanceType: attendance };
      return request;
    });
    if (this.attendanceIs) {
      return { type: BooleanQueryType.OR, args: attendanceRequest } as BooleanQueryRequest;
    } else {
      return { type: BooleanQueryType.NOT, arg: { type: BooleanQueryType.OR, args: attendanceRequest } } as BooleanQueryRequest;
    }
  }

  private getMessage(labels: string[]): string {
    if (this.attendanceIs) {
      return (
        this.translocoService.translate('translations.filter.attendance.name') +
        ' ' +
        this.translocoService.translate('translations.filter.is') +
        ' ' +
        labels.join(' ' + this.translocoService.translate('translations.filter.or') + ' ')
      );
    } else {
      return (
        this.translocoService.translate('translations.filter.attendance.name') +
        ' ' +
        this.translocoService.translate('translations.filter.isNot') +
        ' ' +
        labels.join(' ' + this.translocoService.translate('translations.filter.andNot') + ' ')
      );
    }
  }

  private getSettingsFromFilters() {
    if (this.selectedFilters) {
      const statusFilter = this.selectedFilters.find((f) => f.type === 'ATTENDANCE');
      if (statusFilter) {
        const booleanRequest = statusFilter.request as BooleanQueryRequest;
        if (booleanRequest.type === BooleanQueryType.NOT) {
          this.attendanceIs = false;
          this.selectedAttendance = ((booleanRequest as NotRequest).arg as OrRequest).args.map(
            (r) => (r as AttendanceRequest).attendanceType,
          );
        } else {
          this.attendanceIs = true;
          this.selectedAttendance = (booleanRequest as OrRequest).args.map((r) => (r as AttendanceRequest).attendanceType);
        }
      }
    }
  }

  private initAttendances(): void {
    this.attendances = [
      {
        type: 'REGISTERED',
        value: this.selectedAttendance.includes('REGISTERED'),
        label: this.translocoService.translate('translations.filter.attendance.registered'),
      },
    ];
    if (!this.isPlaylistPageAndTicketExist) {
      this.attendances.push(
        {
          type: 'WAITING_LIST',
          value: this.selectedAttendance.includes('WAITING_LIST'),
          label: this.translocoService.translate('translations.filter.attendance.waitingList'),
        },
        {
          type: 'PARTICIPATED',
          value: this.selectedAttendance.includes('PARTICIPATED'),
          label: this.translocoService.translate('translations.filter.attendance.participated'),
        },
      );
    }
  }
}
